import { lazy, Suspense } from "react";
import { useSectionOrders } from "@/hooks/useSectionOrders";

// Lazy load section components for better performance
const ArticleSection = lazy(() => import("./ArticleSection").then(m => ({ default: m.ArticleSection })));
const PopularCardsSection = lazy(() => import("./PopularCardsSection").then(m => ({ default: m.PopularCardsSection })));
const HomepageArticle = lazy(() => import("./HomepageArticle").then(m => ({ default: m.HomepageArticle })));

interface SectionRendererProps {
  currentPage: "homepage" | "article-listing" | "card-detail" | "article-detail" | "card-listing";
  onViewDetails?: (slug: string) => void;
  onViewAllCards?: () => void;
  sectionsFilter?: (section: { section_type: string; position: number; is_active: boolean }) => boolean;
}

export const SectionRenderer = ({
  currentPage,
  onViewDetails,
  onViewAllCards,
  sectionsFilter,
}: SectionRendererProps) => {
  const { data: sections = [], isLoading } = useSectionOrders(currentPage);

  // Apply filter if provided
  const filteredSections = sectionsFilter 
    ? sections.filter((s) => sectionsFilter({ section_type: s.section_type, position: s.position, is_active: s.is_active ?? false }))
    : sections;

  const renderSection = (sectionType: string) => {
    switch (sectionType) {
      case "article_section":
        return (
          <Suspense key={sectionType} fallback={<div className="h-32 animate-pulse bg-muted" />}>
            <ArticleSection currentPage={currentPage} />
          </Suspense>
        );
      case "popular_cards_section":
        return (
          <Suspense key={sectionType} fallback={<div className="h-64 animate-pulse bg-muted" />}>
            <PopularCardsSection
              currentPage={currentPage}
              onViewDetails={onViewDetails || (() => {})}
              onViewAllCards={onViewAllCards || (() => {})}
            />
          </Suspense>
        );
      case "homepage_article":
        return (
          <Suspense key={sectionType} fallback={<div className="h-96 animate-pulse bg-muted" />}>
            <HomepageArticle 
              articleSlug={currentPage === "card-listing" ? "alle-kredittkort-i-norge-listing" : undefined}
            />
          </Suspense>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      {filteredSections.map((section) => renderSection(section.section_type))}
    </>
  );
};
